import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

const EllipsisPopover = ({
  children,
  popoverContent,
  triggerMode = "click",
  delay = 300,
  visible: externalVisible,
}) => {
  const [internalVisible, setInternalVisible] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [positionStyle, setPositionStyle] = useState({});
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);
  const timeoutRef = useRef(null);

  const selectedLanguage = useSelector((state) => {
    if (state?.i18n?.lang) {
      return state.i18n.lang;
    } else {
      return "en";
    }
  });

  const isVisible =
    externalVisible !== undefined ? externalVisible : internalVisible;
  const togglePopover = (e) => {
    if (externalVisible === undefined) {
      e.stopPropagation();
      setInternalVisible((prev) => !prev);
      setPositioned(false);
    }
  };

  const showPopover = () => {
    if (externalVisible === undefined) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setInternalVisible(true);
        setPositioned(false);
      }, delay);
    }
  };

  const hidePopover = () => {
    if (externalVisible === undefined) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setInternalVisible(false);
      }, delay);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        if (externalVisible === undefined) {
          setInternalVisible(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [externalVisible]);

  useLayoutEffect(() => {
    if (isVisible && !positioned && wrapperRef.current && popoverRef.current) {
      const triggerRect = wrapperRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      const { innerWidth, innerHeight } = window;
      const POPOVER_WIDTH = popoverRect.width;
      const POPOVER_HEIGHT = popoverRect.height;

      const isNearRight = triggerRect.right + POPOVER_WIDTH > innerWidth;
      const isNearBottom = triggerRect.bottom + POPOVER_HEIGHT > innerHeight;

      let top = triggerRect.bottom + 6;
      let left = triggerRect.left;

      if (isNearBottom) {
        top = triggerRect.top - POPOVER_HEIGHT - 6;
      }

      if (isNearRight) {
        left = triggerRect.right - POPOVER_WIDTH;
      }

      if (selectedLanguage === "ar") {
        left = triggerRect.right - POPOVER_WIDTH;
        if (left < 0) {
          left = 0;
        }
      }

      setPositionStyle({
        position: "absolute",
        top: top,
        left: left,
        zIndex: 9999,
      });

      setPositioned(true);
    }
  }, [isVisible, positioned, selectedLanguage]);

  useEffect(() => {
    if (!isVisible || externalVisible !== undefined) return;

    const handleScroll = (event) => {
      const target = event.target;

      const isInsidePopover = popoverRef.current?.contains(target);
      const isInsideTrigger = wrapperRef.current?.contains(target);

      if (!isInsidePopover && !isInsideTrigger) {
        setInternalVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isVisible, externalVisible]);

  return (
    <div
      ref={wrapperRef}
      className={"eliWrapper"}
      onClick={
        triggerMode === "click" && externalVisible === undefined
          ? togglePopover
          : undefined
      }
      onMouseEnter={
        triggerMode === "hover" && externalVisible === undefined
          ? showPopover
          : undefined
      }
      onMouseLeave={
        triggerMode === "hover" && externalVisible === undefined
          ? hidePopover
          : undefined
      }
    >
      <div className={"trigger"}>{children}</div>
      {isVisible &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className={`${"popover"} ${positioned ? "enter" : "hidden"}`}
            style={positionStyle}
            onMouseEnter={
              triggerMode === "hover" && externalVisible === undefined
                ? () => clearTimeout(timeoutRef.current)
                : undefined
            }
            onMouseLeave={
              triggerMode === "hover" && externalVisible === undefined
                ? hidePopover
                : undefined
            }
          >
            {popoverContent}
          </div>,
          document.body
        )}
    </div>
  );
};

export default EllipsisPopover;
